<script setup lang="ts">
    import { ref, computed, onMounted, watch } from 'vue'
    import type { Ref } from 'vue'
    import { useRoute } from 'vue-router'
    import CorporateNavigation from "@common/Components/CorporateNavigation.vue"

    import type { StrategySettingsReadModel } from '@Quest/Strategies/readmodels'

    import { useStrategySettingsQueryV1 } from '@Quest/Strategies/queries-vue-composables'
    import { useUpdateStrategySettingsCommandV1 } from '@Quest/Strategies/commands-vue-composables'

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))

    const currencies = ['AUD', 'BRL', 'CAD', 'EUR', 'GBP', 'JPY', 'USD']

    const {
        runStrategySettingsQueryV1,
        modelStrategySettingsQueryV1
    } = useStrategySettingsQueryV1(workspaceId, strategyId)

    const rm = modelStrategySettingsQueryV1 as Ref<StrategySettingsReadModel | null>

    const {
        invokeUpdateStrategySettingsCommandV1,
        onSucceedUpdateStrategySettingsCommandV1,
        invokingUpdateStrategySettingsCommandV1
    } = useUpdateStrategySettingsCommandV1(workspaceId, strategyId)

    function handleUpdateStrategySettingsCommand()
    {
        if (!rm.value) return

        invokeUpdateStrategySettingsCommandV1({
            name: rm.value.Name,
            description: rm.value.Description,
            currency: rm.value.Currency,
            defaultPnlId: rm.value.DefaultPnlId,
            includeCogs: rm.value.IncludeCogs,
            includeOpex: rm.value.IncludeOpex,
            includeOther: rm.value.IncludeOther,
            fiscalYearStartMonth: rm.value.FiscalYearStartMonth
        })
    }

    onMounted(() =>
    {
        document.title = "Settings | TransparentChoice"
        runStrategySettingsQueryV1()
    })

    onSucceedUpdateStrategySettingsCommandV1(() =>
    {
        alert('Enterprise settings updated successfully')
        runStrategySettingsQueryV1()
    })
</script>

<template>
    <div>
        <CorporateNavigation ActivePage="dashboard" />

        <div v-if="rm" class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Settings</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <button role="button" class="btn btn-success" style="padding: 0.7em 2em;" 
                            @click="handleUpdateStrategySettingsCommand"
                            :disabled="invokingUpdateStrategySettingsCommandV1">Save</button>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div class="form-group">
                <label>Currency:</label>
                <select v-model="rm.Currency" class="form-control">
                    <option v-for="c in currencies" :value="c">{{c}}</option>
                </select>
            </div>

            <div class="form-group">
                <label>Financial Year Start Month:</label>
                <select v-model="rm.FiscalYearStartMonth" class="form-control">
                    <option :value="1">January</option>
                    <option :value="2">February</option>
                    <option :value="3">March</option>
                    <option :value="4">April</option>
                    <option :value="5">May</option>
                    <option :value="6">June</option>
                    <option :value="7">July</option>
                    <option :value="8">August</option>
                    <option :value="9">September</option>
                    <option :value="10">October</option>
                    <option :value="11">November</option>
                    <option :value="12">December</option>
                </select>
            </div>

            <div class="form-group">
                <label>Default P&L:</label>
                <select v-model="rm.DefaultPnlId" class="form-control">
                    <option v-for="t in rm.Pnls" :value="t.Id">{{t.Name}}</option>
                </select>
            </div>

            <hr class="my-4" />

            <fieldset>
                <legend>Project total cost</legend>

                <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="rm.IncludeCogs" id="includeCogs">
                    <label class="form-check-label" for="includeCogs">
                        Include COGS
                    </label>
                </div>
                <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="rm.IncludeOpex" id="includeOpex">
                    <label class="form-check-label" for="includeOpex">
                        Include OPEX
                    </label>
                </div>
                <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="rm.IncludeOther" id="includeOther">
                    <label class="form-check-label" for="includeOther">
                        Include Other Expenses
                    </label>
                </div>
            </fieldset>
        </div>
    </div>
</template>


